import { parseNumber } from '../warehouse/warehouse.utils';
import { CardPayment, DealPaymentStatus, PaymentInvoice, PaymentStatus } from './payments.types';

/** Badge classes of the status of one payment */
export const PAYMENT_STATUS_CLASSES: Record<PaymentStatus, string> = {
  pending: 'bg-amber-50 text-amber-700',
  paid: 'bg-emerald-50 text-emerald-700',
  canceled: 'bg-slate-100 text-slate-500',
};

/** Badge and progress bar classes of the payment status of a deal */
export const DEAL_PAYMENT_STATUS_STYLES: Record<DealPaymentStatus, { badge: string; bar: string }> = {
  unpaid: { badge: 'bg-rose-50 text-rose-700', bar: 'bg-rose-400' },
  partially_paid: { badge: 'bg-amber-50 text-amber-700', bar: 'bg-amber-400' },
  paid: { badge: 'bg-emerald-50 text-emerald-700', bar: 'bg-emerald-500' },
  overpaid: { badge: 'bg-violet-50 text-violet-700', bar: 'bg-violet-500' },
};

export const PAYMENT_COMMENT_MAX_LENGTH = 2000;
export const PAYMENT_DIRECTORY_NAME_MAX_LENGTH = 255;

/** The date a payment is listed and filtered by: when it was paid, or created while it has no date */
export const getPaymentDate = (payment: Pick<CardPayment, 'paid_at' | 'created_at'>): string =>
  payment.paid_at ?? payment.created_at;

/** Two decimals, as the backend compares the amounts */
export const roundMoney = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;

/** What is left to pay, and the overpayment when more is paid than the deal costs */
export function getDealBalance(price: number | null | undefined, paidTotal: number | null | undefined) {
  const difference = roundMoney((paidTotal ?? 0) - (price ?? 0));
  return { remaining: Math.max(-difference, 0), overpaid: Math.max(difference, 0) };
}

/** Share of the deal price that is paid, 0..100, 0 for a deal without a price */
export function getPaidPercent(price: number | null | undefined, paidTotal: number | null | undefined): number {
  if (!price || price <= 0 || !paidTotal || paidTotal <= 0) return 0;

  return Math.min(Math.round((paidTotal / price) * 100), 100);
}

/** A typed amount: from 0.01, up to 2 decimals, a comma is a decimal point; null when it is not valid */
export function parsePaymentAmount(value: string | null | undefined): number | null {
  const amount = parseNumber(value, 2);
  return amount !== null && amount >= 0.01 ? amount : null;
}

/** Only a pending payment can be edited */
export const isPaymentEditable = (payment: Pick<CardPayment, 'status' | 'fiscal_operation'>): boolean =>
  payment.status === 'pending' && !payment.fiscal_operation;

/** A pending or a paid payment can be canceled, a canceled one is final */
export const isPaymentCancelable = (payment: Pick<CardPayment, 'status' | 'fiscal_operation'>): boolean =>
  payment.status !== 'canceled' && !payment.fiscal_operation;

/** Days a payment link of the `bank_qr` integration lives: 7 by default, up to a year */
export const BANK_QR_LINK_TTL_DEFAULT = 7;
export const BANK_QR_LINK_TTL_MAX = 365;
export const BANK_QR_PURPOSE_MAX_LENGTH = 140;

/** An IBAN as it is stored: without spaces, in upper case */
export const normalizeIban = (iban: string | null | undefined): string => (iban ?? '').replace(/\s+/g, '').toUpperCase();

/** A Ukrainian IBAN: `UA`, 27 digits and a valid ISO 13616 check sum */
export function isUaIban(value: string | null | undefined): boolean {
  const iban = normalizeIban(value);
  if (!/^UA\d{27}$/.test(iban)) return false;

  // The country and the check digits go to the end, the letters become numbers (A = 10), the rest of 97 must be 1
  const digits = `${iban.slice(4)}${iban.slice(0, 4)}`.replace(/[A-Z]/g, (letter) => String(letter.charCodeAt(0) - 55));
  return [...digits].reduce((rest, digit) => (rest * 10 + Number(digit)) % 97, 0) === 1;
}

/** ЄДРПОУ of a company (8 digits) or РНОКПП of a person (10 digits) */
export const isTaxId = (value: string | null | undefined): boolean => /^(\d{8}|\d{10})$/.test((value ?? '').trim());

/** Limits of the invoice details the backend checks: the recipient name of a payment QR code is up to 70 characters */
export const BANK_QR_NAME_MAX_LENGTH = 70;

/** The invoice of a payment that can still be paid by its link: a pending payment only */
export const getPayableInvoice = (payment: Pick<CardPayment, 'status' | 'x_data'>): PaymentInvoice | null =>
  payment.status === 'pending' ? (payment.x_data?.invoice ?? null) : null;

export const isInvoiceExpired = (invoice: Pick<PaymentInvoice, 'expires_at'>): boolean =>
  new Date(invoice.expires_at).getTime() < Date.now();

/** The row whose link was just copied, for a moment: its action shows a check instead of the link icon */
export class CopiedMark {
  id: string | null = null;
  private timer?: ReturnType<typeof setTimeout>;

  constructor(private readonly durationMs = 2000) {}

  mark(id: string): void {
    clearTimeout(this.timer);
    this.id = id;
    this.timer = setTimeout(() => (this.id = null), this.durationMs);
  }

  destroy(): void {
    clearTimeout(this.timer);
  }
}
